<template>
  <BaseVeeForm :action="handleSubmit">
    <template #default="formProps">
      <VModal disable-backdrop-click :disable-close="formProps.isPending" @close="$emit('close')">
        <template #default="modalProps">
          <div class="rounded shadow-xs w-full max-w-lg h-full mx-auto">
            <div class="rounded shadow-lg bg-gray-100 overflow-hidden h-full flex flex-col">
              <div class="px-4 sm:px-6 py-5 border-b flex-none">
                <div class="flex items-center justify-between">
                  <div class="flex-1">
                    <h1 class="text-sm">Custom email service</h1>
                  </div>

                  <div class="flex-none">
                    <button
                      type="button"
                      class="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      @click="modalProps.close"
                    >
                      <span class="sr-only">Close</span>
                      <svg
                        class="inline-flex h-6 w-6"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex-1 bg-white overflow-auto px-4 sm:px-6 space-y-12">
                <!-- General settings -->
                <section class="py-5">
                  <div class="space-y-4">
                    <BaseFormTextFieldWithValidator
                      v-bind="configNameFieldProps"
                      v-model="formData.config.name"
                      class="max-w-sm"
                    />
                    <BaseFormTextFieldWithValidator
                      v-bind="configAddressFieldProps"
                      v-model="formData.config.email_address"
                      class="max-w-sm"
                    />
                    <BaseFormTextFieldWithValidator
                      v-bind="configPasswordFieldProps"
                      v-model="formData.config.password"
                      class="max-w-sm"
                    />
                    <div class="space-y-2">
                      <BaseFormCheckbox v-model="formData.personal">
                        Personal service
                      </BaseFormCheckbox>
                      <BaseFormInlineMessage info>
                        If checked only you will have access to this service.
                      </BaseFormInlineMessage>
                    </div>
                  </div>
                </section>

                <!-- Incoming server settings -->
                <section class="py-5">
                  <h3 class="text-lg font-medium">Incoming server settings</h3>
                  <div class="mt-6 space-y-4">
                    <div class="flex gap-4 max-w-lg">
                      <BaseFormTextFieldWithValidator
                        v-bind="configIncomingServerHostFieldProps"
                        v-model="formData.config.incoming_server.host"
                        class="flex-1"
                      />
                      <BaseFormTextFieldWithValidator
                        v-bind="configIncomingServerPortFieldProps"
                        v-model="formData.config.incoming_server.port"
                        class="w-24"
                      />
                      <BaseFormSelectFieldWithValidator
                        v-bind="configIncomingServerEncryptionFieldProps"
                        v-model="formData.config.incoming_server.encryption"
                        class="w-32"
                      />
                    </div>
                    <div class="space-y-2">
                      <BaseFormTextFieldWithValidator
                        v-if="useDifferentUsernameForIncomingServer"
                        v-bind="configIncomingServerUsernameFieldProps"
                        v-model="formData.config.incoming_server.username"
                        class="max-w-sm"
                      />
                      <BaseFormGroup v-else>
                        <BaseFormLabel> Username </BaseFormLabel>
                        <BaseFormInput
                          type="text"
                          :value="formData.config.email_address"
                          readonly
                          class="max-w-sm"
                        />
                      </BaseFormGroup>
                      <BaseFormCheckbox v-model="useDifferentUsernameForIncomingServer">
                        Use different username
                      </BaseFormCheckbox>
                    </div>
                  </div>
                </section>

                <!-- Outgoing server settings -->
                <section class="py-5">
                  <h3 class="text-lg font-medium">Outgoing server settings</h3>
                  <div class="mt-6 space-y-4">
                    <div class="flex gap-4 max-w-lg">
                      <BaseFormTextFieldWithValidator
                        v-bind="configOutgoingServerHostFieldProps"
                        v-model="formData.config.outgoing_server.host"
                        class="flex-1"
                      />
                      <BaseFormTextFieldWithValidator
                        v-bind="configOutgoingServerPortFieldProps"
                        v-model="formData.config.outgoing_server.port"
                        class="w-24"
                      />
                      <BaseFormSelectFieldWithValidator
                        v-bind="configOutgoingServerEncryptionFieldProps"
                        v-model="formData.config.outgoing_server.encryption"
                        class="w-32"
                      />
                    </div>
                    <div class="space-y-2">
                      <BaseFormTextFieldWithValidator
                        v-if="useDifferentUsernameForOutgoingServer"
                        v-bind="configOutgoingServerUsernameFieldProps"
                        v-model="formData.config.outgoing_server.username"
                        class="max-w-sm"
                      />
                      <BaseFormGroup v-else>
                        <BaseFormLabel> Username </BaseFormLabel>
                        <BaseFormInput
                          type="text"
                          :value="formData.config.email_address"
                          readonly
                          class="max-w-sm"
                        />
                      </BaseFormGroup>
                      <BaseFormCheckbox v-model="useDifferentUsernameForOutgoingServer">
                        Use different username
                      </BaseFormCheckbox>
                    </div>
                    <BaseFormTextFieldWithValidator
                      v-bind="configOutgoingServerMaxSendPerHourFieldProps"
                      v-model="formData.config.outgoing_server.max_send_per_hour"
                    />
                  </div>
                </section>

                <!-- Bounce server settings -->
                <section class="py-5">
                  <h3 class="text-lg font-medium mb-4">Bounce server settings</h3>
                  <BaseFormCheckbox v-model="useDifferentBounceServer">
                    Use different server for handling bounced emails
                  </BaseFormCheckbox>
                  <div v-if="useDifferentBounceServer" class="mt-6 space-y-4">
                    <div class="flex gap-4 max-w-lg">
                      <BaseFormTextFieldWithValidator
                        v-bind="configBounceServerHostFieldProps"
                        v-model="formData.config.bounce_server.host"
                        class="flex-1"
                      />
                      <BaseFormTextFieldWithValidator
                        v-bind="configBounceServerPortFieldProps"
                        v-model="formData.config.bounce_server.port"
                        class="w-24"
                      />
                      <BaseFormTextFieldWithValidator
                        v-bind="configBounceServerEncryptionFieldProps"
                        v-model="formData.config.bounce_server.encryption"
                        class="w-32"
                      />
                    </div>
                    <BaseFormTextFieldWithValidator
                      v-bind="configBounceServerEmailAddressFieldProps"
                      v-model="formData.config.bounce_server.email_address"
                      class="max-w-sm"
                    />
                    <BaseFormTextFieldWithValidator
                      v-bind="configBounceServerPasswordFieldProps"
                      v-model="formData.config.bounce_server.password"
                      class="max-w-sm"
                    />
                  </div>
                  <div v-else class="mt-6 rounded-md bg-blue-50 p-4">
                    <div class="flex">
                      <div class="flex-shrink-0">
                        <svg
                          class="h-5 w-5 text-blue-400"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M19 10.5a8.5 8.5 0 11-17 0 8.5 8.5 0 0117 0zM8.25 9.75A.75.75 0 019 9h.253a1.75 1.75 0 011.709 2.13l-.46 2.066a.25.25 0 00.245.304H11a.75.75 0 010 1.5h-.253a1.75 1.75 0 01-1.709-2.13l.46-2.066a.25.25 0 00-.245-.304H9a.75.75 0 01-.75-.75zM10 7a1 1 0 100-2 1 1 0 000 2z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </div>
                      <div class="ml-3">
                        <p class="text-sm text-blue-700">
                          Incoming server will be used for handling bounced emails.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
              <div class="flex-none bg-white px-4 sm:px-6 py-4 sm:py-5 border-t">
                <BaseButton
                  :primary="formProps.isIdle || formProps.isPending"
                  :success="formProps.isResolved"
                  :danger="formProps.isRejected"
                  :disabled="formProps.invalid || formProps.isPending"
                >
                  <span v-if="formProps.isPending">Saving...</span>
                  <span v-else-if="formProps.isResolved">Saved</span>
                  <span v-else-if="formProps.isRejected">Failed</span>
                  <span v-else>Save</span>
                </BaseButton>
              </div>
            </div>
          </div>
        </template>
      </VModal>
    </template>
  </BaseVeeForm>
</template>

<script>
import { accountCustomEmailIntegrationsClient, customEmailIntegrationsClient } from '@/api/clients';
import { unset, get, has } from 'lodash-es';
export default {
  name: 'CustomEmailServiceDialog',
  props: {
    accountId: {
      type: String,
      required: true,
    },
    customEmailIntegrationId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      useDifferentBounceServer: false,
      useDifferentUsernameForIncomingServer: false,
      useDifferentUsernameForOutgoingServer: false,
      formData: {
        personal: false,
        config: {
          name: '',
          email_address: '',
          password: '',
          outgoing_server: {
            host: '',
            port: '',
            encryption: 'ssl',
            max_send_per_hour: 200,
          },
          incoming_server: {
            host: '',
            port: '',
            encryption: 'ssl',
          },
          bounce_server: {
            host: '',
            port: '',
            encryption: 'ssl',
            email_address: '',
            password: '',
          },
        },
      },
    };
  },
  computed: {
    configNameFieldProps() {
      return {
        validator: {
          name: 'name',
          vid: 'name',
          rules: { required: true },
        },
        field: {
          id: 'custom_email_name',
          label: 'Full name',
        },
      };
    },
    configAddressFieldProps() {
      return {
        validator: {
          name: 'email address',
          vid: 'email_address',
          rules: { required: true },
        },
        field: {
          id: 'custom_email_email_address',
          inputType: 'email',
          label: 'Email address',
        },
      };
    },
    configPasswordFieldProps() {
      return {
        validator: {
          name: 'password',
          vid: 'password',
          rules: {
            required: !!this.customEmailIntegrationId === false,
          },
        },
        field: {
          id: 'custom_email_password',
          inputType: 'password',
          label: 'Password',
          attrs: {
            autocomplete: 'new-password',
          },
        },
      };
    },
    configOutgoingServerHostFieldProps() {
      return {
        validator: {
          name: 'host',
          vid: 'config.outgoing_server.host',
          rules: { required: true },
        },
        field: {
          id: 'custom_email_outgoing_server_host',
          label: 'Host',
        },
      };
    },
    configOutgoingServerPortFieldProps() {
      return {
        validator: {
          name: 'port',
          vid: 'config.outgoing_server.port',
          rules: { required: true },
        },
        field: {
          id: 'custom_email_outgoing_server_port',
          inputType: 'number',
          label: 'Port',
        },
      };
    },
    configOutgoingServerEncryptionFieldProps() {
      return {
        validator: {
          name: 'encryption',
          vid: 'config.outgoing_server.encryption',
          rules: { required: true },
        },
        field: {
          id: 'custom_email_outgoing_server_encryption',
          label: 'Encryption',
          options: [
            { value: 'ssl', label: 'SSL' },
            { value: 'tls', label: 'TLS' },
          ],
        },
      };
    },
    configOutgoingServerUsernameFieldProps() {
      return {
        validator: {
          name: 'username',
          vid: 'username',
          rules: { required: true },
        },
        field: {
          id: 'custom_email_outgoing_server_username',
          label: 'Username',
        },
      };
    },
    configOutgoingServerMaxSendPerHourFieldProps() {
      return {
        validator: {
          name: 'max send per hour',
          vid: 'config.outgoing_server.max_send_per_hour',
          rules: { required: true },
        },
        field: {
          id: 'custom_email_outgoing_server_max_send_per_hour',
          inputType: 'number',
          inputClasses: 'w-32',
          label: 'Max send / Hour',
          instructions:
            'The total emails that can be sent within an hour. Please advise your email service provider.',
        },
      };
    },
    configIncomingServerHostFieldProps() {
      return {
        validator: {
          name: 'host',
          vid: 'config.incoming_server.host',
          rules: { required: true },
        },
        field: {
          id: 'custom_email_incoming_server_host',
          label: 'Host',
        },
      };
    },
    configIncomingServerPortFieldProps() {
      return {
        validator: {
          name: 'port',
          vid: 'config.incoming_server.port',
          rules: { required: true },
        },
        field: {
          id: 'custom_email_incoming_server_port',
          inputType: 'number',
          label: 'Port',
        },
      };
    },
    configIncomingServerEncryptionFieldProps() {
      return {
        validator: {
          name: 'encryption',
          vid: 'config.incoming_server.encryption',
          rules: { required: true },
        },
        field: {
          id: 'custom_email_incoming_server_encryption',
          label: 'Encryption',
          options: [
            { value: 'ssl', label: 'SSL' },
            { value: 'tls', label: 'TLS' },
          ],
        },
      };
    },
    configIncomingServerUsernameFieldProps() {
      return {
        validator: {
          name: 'username',
          vid: 'username',
          rules: { required: true },
        },
        field: {
          id: 'custom_email_incoming_server_username',
          label: 'Username',
        },
      };
    },
    configBounceServerHostFieldProps() {
      return {
        validator: {
          name: 'host',
          vid: 'config.bounce_server.host',
          rules: { required: true },
        },
        field: {
          id: 'custom_email_bounce_server_host',
          label: 'Host',
        },
      };
    },
    configBounceServerPortFieldProps() {
      return {
        validator: {
          name: 'port',
          vid: 'config.bounce_server.port',
          rules: { required: true },
        },
        field: {
          id: 'custom_email_bounce_server_port',
          inputType: 'number',
          label: 'Port',
        },
      };
    },
    configBounceServerEncryptionFieldProps() {
      return {
        validator: {
          name: 'encryption',
          vid: 'config.bounce_server.encryption',
          rules: { required: true },
        },
        field: {
          id: 'custom_email_bounce_server_encryption',
          label: 'Encryption',
        },
      };
    },
    configBounceServerEmailAddressFieldProps() {
      return {
        validator: {
          name: 'email address',
          vid: 'email_address',
          rules: { required: true },
        },
        field: {
          id: 'custom_email_bounce_server_email_address',
          label: 'Email address',
        },
      };
    },
    configBounceServerPasswordFieldProps() {
      return {
        validator: {
          name: 'password',
          vid: 'password',
          rules: {
            required: !!this.customEmailIntegrationId === false,
          },
        },
        field: {
          id: 'custom_email_bounce_server_password',
          inputType: 'password',
          label: 'Password',
          attrs: {
            autocomplete: 'new-password',
          },
        },
      };
    },
  },
  created() {
    if (this.customEmailIntegrationId) {
      this.fetchCustomEmailIntegration();
    }
  },
  methods: {
    async fetchCustomEmailIntegration() {
      try {
        const response = await customEmailIntegrationsClient.read(this.customEmailIntegrationId);

        this.formData.personal = response.data.personal_third_party_service;
        this.formData.config.name = get(response.data, 'config.name', '');
        this.formData.config.email_address = get(response.data, 'config.email_address', '');
        this.formData.config.outgoing_server.host = get(
          response.data,
          'config.outgoing_server.host',
          ''
        );
        this.formData.config.outgoing_server.port = get(
          response.data,
          'config.outgoing_server.port',
          ''
        );
        this.formData.config.outgoing_server.encryption = get(
          response.data,
          'config.outgoing_server.encryption',
          'ssl'
        );
        this.formData.config.outgoing_server.max_send_per_hour = get(
          response.data,
          'config.outgoing_server.max_send_per_hour',
          200
        );

        if (has(response.data, 'config.outgoing_server.username')) {
          this.formData.config.outgoing_server.username = get(
            response.data,
            'config.outgoing_server.username',
            ''
          );
          this.useDifferentUsernameForOutgoingServer = true;
        }

        this.formData.config.incoming_server.host = get(
          response.data,
          'config.incoming_server.host',
          ''
        );
        this.formData.config.incoming_server.port = get(
          response.data,
          'config.incoming_server.port',
          ''
        );
        this.formData.config.incoming_server.encryption = get(
          response.data,
          'config.incoming_server.encryption',
          'ssl'
        );

        if (has(response.data, 'config.incoming_server.username')) {
          this.formData.config.incoming_server.username = get(
            response.data,
            'config.incoming_server.username',
            ''
          );
          this.useDifferentUsernameForIncomingServer = true;
        }

        if (has(response.data, 'config.bounce_server')) {
          this.formData.config.bounce_server.host = get(
            response.data,
            'config.bounce_server.host',
            ''
          );
          this.formData.config.bounce_server.port = get(
            response.data,
            'config.bounce_server.port',
            ''
          );
          this.formData.config.bounce_server.encryption = get(
            response.data,
            'config.bounce_server.encryption',
            'ssl'
          );
          this.formData.config.bounce_server.email_address = get(
            response.data,
            'config.bounce_server.email_address',
            ''
          );
          this.formData.config.bounce_server.password = get(
            response.data,
            'config.bounce_server.password',
            ''
          );
          this.useDifferentBounceServer = true;
        }
      } catch (error) {
        //
      }
    },
    handleSubmit() {
      const formData = JSON.parse(JSON.stringify(this.formData));

      if (this.useDifferentBounceServer === false) {
        unset(formData, 'config.bounce_server');
      }

      if (this.useDifferentUsernameForIncomingServer === false) {
        unset(formData, 'config.incoming_server.username');
      }

      if (this.useDifferentUsernameForOutgoingServer === false) {
        unset(formData, 'config.outgoing_server.username');
      }

      if (this.customEmailIntegrationId) {
        return this.updateCustomEmailIntegration(formData);
      }

      return this.storeCustomEmailIntegration(formData);
    },
    storeCustomEmailIntegration(data) {
      return accountCustomEmailIntegrationsClient.store(this.accountId, data);
    },
    updateCustomEmailIntegration(data) {
      return customEmailIntegrationsClient.update(this.customEmailIntegrationId, data);
    },
  },
};
</script>
